import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Log} from "ng2-logger";

import {OperationType} from '@app/tool/model/operationType.model';
import {OperationTypeService} from '@services/operation-type.service';
import {Ticket} from '@models/ticket.model';

import {AppUtils} from "@app/shared/util/app.utils";
import {WizardModalComponent} from "@components/wizard-modal/wizard-modal.component";
import {ToolScanTreeComponent} from '@app/tool/components';
import {TableConfig} from '@app/shared/util/table-config.utils';
import {ToolReturn} from "@models/tool-return.model";
import {TicketType} from "@models/enums/ticket-type.enum";
import {ToolUsageService} from '@app/core/services/tool-usage.service';
import {TicketStatus} from "@models/enums/ticket-status";
import {TreeModel} from "@components/tree/tree.model";
import {TranslateService} from "@ngx-translate/core";
import {MaintenancePlansService} from '@app/maintenance/service/maintenance-plans.service';
import {MaintenancePlanUtils} from '@app/shared/util/maintenance-plan.utils';
import {ActivatedRoute, Router} from '@angular/router';
import {AuthenticationService} from '@app/authentication/services/authentication.service';
import {Role} from '@app/shared/models/roles.enum';
import { RevisionInfo } from "@components/revision-info/models/revision-info.model";
import { RevisionInfoModalComponent } from "@components/revision-info-modal/revision-info-modal.component";

@Component({
    selector: 'app-ticket-create-modal',
    templateUrl: './ticket-create-modal.component.html',
    styleUrls: ['./ticket-create-modal.component.scss']
})
export class TicketCreateModalComponent implements OnInit, AfterViewInit {
    _logger = Log.create(TicketCreateModalComponent.name);

    @ViewChild(WizardModalComponent, {static: true}) modal: WizardModalComponent;
    @ViewChild(ToolScanTreeComponent, {static: true}) toolScanTree: ToolScanTreeComponent;
    @ViewChild(RevisionInfoModalComponent, {static: true}) revisionInfoModal: RevisionInfoModalComponent;

    @Output() create: EventEmitter<any> = new EventEmitter();
    @Output() closeModal: EventEmitter<any> = new EventEmitter();
    @Output() ticketCheck: EventEmitter<any> = new EventEmitter();

    @Input() similarTickets: TreeModel[];
    @Input() isLoading: boolean;
    @Input() config: TableConfig;


    public ticket: Ticket = new Ticket();

    selectToolsForm: UntypedFormGroup;
    operationsForm: UntypedFormGroup;
    operationsWithRecurrencesForm: UntypedFormGroup;

    showValidations = false;
    selectedTools: any = [];
    selectedRecurrencesItems: any = [];
    operationTypes: OperationType[] = [];
    availableOperations: OperationType[] = [];
    unavailableOperations: OperationType[] = [];

    operationsWithRecurrences = [];
    maintenancePlanItems = [];
    selectedOperations = [];
    mandatoryMaintenancePlanItems = [];

    ticketOperations = [];
    overviewItems = [];

    toolReturn: ToolReturn = null;
    ticketType: TicketType;
    revisionInfo: RevisionInfo[] = [];

    firstTool: any;

    constructor(private operationTypeService: OperationTypeService,
                private translateService: TranslateService,
                private toolUsageService: ToolUsageService,
                private authService: AuthenticationService,
                private router: Router,
                private route: ActivatedRoute,
                private maintenancePlanService: MaintenancePlansService) {
    }

    ngOnInit() {
        this.onFormInit();
        this.getOperationTypes();
    }

    ngAfterViewInit() {
    }

    open(toolReturn?: ToolReturn,
         ticketType: TicketType = null,
         unavailableOperations?: OperationType[]) {
        this.toolReturn = toolReturn;
        this.ticketType = ticketType;
        this.unavailableOperations = unavailableOperations;

        this.selectedTools = [];
        this.selectedRecurrencesItems = [];
        this.maintenancePlanItems = [];
        this.operationsWithRecurrences = [];
        this.selectedOperations = [];
        this.ticketOperations = [];

        this.onFormInit();
        this.modal.open();
    }

    close() {
        this.modal.close();
        this.closeModal.emit();
        this.resetData();
    }

    onFormInit() {
        const ticketId = 0;
        const ticketDescription = '';
        const ticketOperationTypes = [];
        const selectedTools = [];
        const hasPriority = false;

        if (!this.selectToolsForm) {
            this.selectToolsForm = new UntypedFormGroup({
                'id': new UntypedFormControl(ticketId),
                'selectedTools': new UntypedFormControl(selectedTools, [AppUtils.atLeastOneToolValidator(1)])
            });
        }

        this.operationsForm = new UntypedFormGroup({
            'description': new UntypedFormControl(ticketDescription, [Validators.required]),
            'operationTypes': new UntypedFormControl(ticketOperationTypes, [AppUtils.notEmptyValidation]),
            'hasPriority': new UntypedFormControl(hasPriority, [Validators.required])
        });

        this.operationsWithRecurrencesForm = new UntypedFormGroup({
            'selectedRecurrencesItems': new UntypedFormControl([], [AppUtils.atLeastOneToolValidator(1)])
        });

        this._logger.info('onFormInit:: form initialised', this.selectToolsForm, this.operationsForm);
    }

    onStepChange(step) {
        this._logger.info('onStepChange:: ', step);

        if (!this.toolsHaveMaintenancePlan() && step === 2) {
            step = 3;
        }

        if (step === 1) {
            if (this.selectToolsForm.controls.selectedTools.value) {
                const toolsIds = this.selectToolsForm.controls.selectedTools.value.map(x => x.data.id);
                const tableConfig = new TableConfig();
                toolsIds.forEach(element => {
                    tableConfig.filters.push({field: 'toolIds', value: element})
                });
                this.isLoading = true;

                this.availableOperations = this.operationTypes;

                if (this.unavailableOperations) {
                    this.availableOperations = this.availableOperations.filter(
                        x => this.unavailableOperations.find(
                            y => y.name === x.name
                        ) === undefined
                    );
                }

                this.maintenancePlanService.getMandatoryPlanItemsByToolIds(toolsIds).subscribe((data: any) => {
                    this.mandatoryMaintenancePlanItems = data;
                    if (this.mandatoryMaintenancePlanItems && this.mandatoryMaintenancePlanItems.length) {
                        this.operationsForm.get('operationTypes').clearValidators();
                        this.operationsForm.get('operationTypes').updateValueAndValidity();
                    }

                    this.isLoading = false;
                });

                this.toolUsageService.getProductionData(toolsIds).subscribe((data: RevisionInfo[]) => {
                    this.revisionInfo = data;

                    this.isLoading = false;
                });
            }
        }
        if (step === 2) {
            this.selectedOperations = this.operationsForm.controls.operationTypes.value.map(x => x.id);
            const toolIds = this.selectToolsForm.controls.selectedTools.value.map(x => x.data.id);
            this.isLoading = true;

            this.maintenancePlanService.getPlanItemsByToolIds(toolIds).subscribe((data: any) => {
                this.maintenancePlanItems = data;

                // Obtain all the  operation (selected and mandatory)
                const allOperationIds = this.mandatoryMaintenancePlanItems && this.mandatoryMaintenancePlanItems.length > 0
                    ? this.selectedOperations.concat(this.mandatoryMaintenancePlanItems.map(item => item.operationId))
                    : this.selectedOperations;

                // Transform selected operations in operations with recurrences
                this.operationsWithRecurrences = MaintenancePlanUtils.groupMaintenancePlanItems(this.maintenancePlanItems,
                    this.mandatoryMaintenancePlanItems,
                    allOperationIds,
                    this.translateService.instant('admin.toolMaintenancePlans.recurrences.recurringEvery'));

                // Initialed the operationsWithRecurrencesForm with preselected recurrences
                this.operationsWithRecurrencesForm.setValue({
                    'selectedRecurrencesItems': MaintenancePlanUtils.groupSelectedOperations(this.operationsWithRecurrences)
                });

                this.isLoading = false;

            });
        }
        if (step === 3) {
            this.ticketCheck.emit({
                description: this.operationsForm.controls.description.value,
                operationTypes: this.operationsForm.controls.operationTypes.value,
                hasPriority: this.operationsForm.controls.hasPriority.value,
                selectedTools: this.selectToolsForm.controls.selectedTools.value
            });

            if (this.toolsHaveMaintenancePlan()) {
                const groupedTools = MaintenancePlanUtils.groupTools(this.selectToolsForm.controls.selectedTools.value);
                const operationsWithSelectedRecurrences = this.operationsWithRecurrencesForm.controls.selectedRecurrencesItems.value;

                this.ticketOperations = MaintenancePlanUtils.computeTicketOperations(groupedTools,
                    operationsWithSelectedRecurrences,
                    this.maintenancePlanItems)

                this.overviewItems = MaintenancePlanUtils.buildOverviewItems(groupedTools,
                    operationsWithSelectedRecurrences,
                    this.selectToolsForm.controls.selectedTools.value,
                    this.maintenancePlanItems,
                    this.translateService.instant(
                        'admin.toolMaintenancePlans.recurrences.recurringEvery'
                    ));
            }
        } else {
            this.similarTickets = [];
        }
    }

    onCancel() {
        this.resetData();
        this.closeModal.emit();
        this.modal.close();
    }

    onStepErrosChange() {
        this.showValidations = !this.operationsForm.valid;
    }

    onSubmit() {
        if (!this.operationsForm.valid) {
            this.showValidations = true;
            return;
        }

        this._logger.info('onSubmit::', this.selectToolsForm, this.operationsForm);
        this._logger.info('createTicket:: Form is valid', this.selectToolsForm);

        this.create.emit({
            description: this.operationsForm.controls.description.value,
            operationTypes: MaintenancePlanUtils.computeOperationTypes(this.operationsForm.controls.operationTypes.value,
                                                                       this.mandatoryMaintenancePlanItems,
                                                                       this.operationTypes),
            ticketOperations: this.ticketOperations,
            hasPriority: this.operationsForm.controls.hasPriority.value,
            selectedTools: this.selectToolsForm.controls.selectedTools.value,
            type: this.ticketType
        });
    }

    resetData() {
        this._logger.info('resetForm:: Resetting form');

        this.selectToolsForm.reset();
        this.operationsForm.reset();
        this.operationsWithRecurrencesForm.reset();

        this.showValidations = false;

        this.selectedTools = [];
        this.selectedRecurrencesItems = [];
        this.maintenancePlanItems = [];
        this.operationsWithRecurrences = [];
        this.selectedOperations = [];
        this.ticketOperations = [];

        this.firstTool = null;
        this.config = new TableConfig();

        this.modal.reset();
    }

    isOpen() {
        return this.modal?.isOpen;
    }

    hasSimilarTicketsInProgress() {
        const similarTicketsInProgress = this.similarTicketsInProgress();
        return similarTicketsInProgress && similarTicketsInProgress.length > 0;
    }

    computeSimilarTickets() {
        const similarTicketsInProgress = this.similarTicketsInProgress();
        if (similarTicketsInProgress && similarTicketsInProgress.length) {
            return similarTicketsInProgress;
        }

        return this.similarTicketsNew();
    }

    computeOverviewStepDescription() {
        const similarTicketsInProgress = this.similarTicketsInProgress();
        if (similarTicketsInProgress && similarTicketsInProgress.length) {
            return this.translateService.instant('admin.tickets.create-modal.overview.error-message');
        }

        const similarTicketsNew = this.similarTicketsNew();
        if (similarTicketsNew && similarTicketsNew.length) {
            return this.translateService.instant('admin.tickets.create-modal.overview.modal-description');
        }

        return '';
    }

    goToPlan(planId: number) {
        let path = '';
        if (this.authService.isCurrentUserWithRole(Role.ADMIN)) {
            path = '/tool-maintenance-plans';
        } else if (this.authService.isCurrentUserWithRole(Role.ATOM_OPERATOR)
            || this.authService.isCurrentUserWithRole(Role.SERVICE_OPERATOR)) {
            path = '/read-tool-maintenance-plans';
        }

        const url = this.router.serializeUrl(
            this.router.createUrlTree(
                [`${path}/plans/${planId}/details/`],
                {relativeTo: this.route}
            ));
        localStorage.setItem('openedInNewTab', 'true');
        window.open(url);
    }

    canContinueStep1() {
        return this.operationsForm.valid
            || (this.mandatoryMaintenancePlanItems.length > 0 && this.operationsForm.get('description').valid);
    }

    private getOperationTypes() {
        // Observable for retrieving operation types data
        this._logger.error('getOperationTypes:: Retrieving operation types');
        this.operationTypeService.getAllOperationTypes().subscribe(
            (data: any) => {
                this._logger.error('getOperationTypes:: API returned data', data);
                this.operationTypes = data.operationTypes;
            },
            (error: any) => {
                this._logger.error('API returned error', error);
            }
        );
    }

    private similarTicketsInProgress() {
        return this.similarTickets ? this.similarTickets.filter(y => y.data.ticketStatus === TicketStatus.READY
            || y.data.ticketStatus === TicketStatus.WAITING_FOR_TOOLS
            || y.data.ticketStatus === TicketStatus.IN_PROGRESS) : []
    }

    private similarTicketsNew() {
        return this.similarTickets ? this.similarTickets.filter(y => y.data.ticketStatus === TicketStatus.UNAVAILABLE
            || y.data.ticketStatus === TicketStatus.NEW) : [];
    }

    onToolScan($event) {
        if (this.firstTool) {
            return;
        }

        this.firstTool = $event.data;
        const needsMaintenancePlan = this.firstTool && this.firstTool.maintenancePlanId != null;

        this.config.filters.push({field: 'needsMaintenancePlan', value: needsMaintenancePlan});
    }

    getInfoMessage() {
        if (!this.firstTool) {
            return '';
        }

        if (this.firstTool.maintenancePlanId != null) {
            return this.translateService.instant('admin.tickets.create-modal.info.toolsWithMaintenancePlan');
        } else {
            return this.translateService.instant('admin.tickets.create-modal.info.toolsWithoutMaintenancePlan');
        }
    }

    toolsHaveMaintenancePlan() {
        return this.firstTool && this.firstTool.maintenancePlanId != null;
    }

    openRevisionModal() {
        this.revisionInfoModal.open(this.revisionInfo);
    }
}
